import { useCallback, useEffect, useRef, useState } from "react";
import api from '@helpers/api';

import { AddRounded, DeleteRounded, EditRounded, ListAltRounded } from "@mui/icons-material";
import { Button } from "@fragments";


export default function useIntegrations(info, setPageActions) {
    const [loading, setLoading] = useState(true);
    const [integrations, setIntegrations] = useState([]);
    const [pagination, setPagination] = useState({ pages: 0, records: 0, page: 1 });

    const popperRef = useRef();
    const manageModalRef = useRef();
    const logsModalRef = useRef();

    useEffect(() => {
        if (info?.id) handleLoad(1);
    }, [info?.id]);

    const handleLoad = useCallback(async (page = pagination.page) => {
        setLoading(true);
        try {
            if (info?.id) {
                const res = await api.get(`community/${info.id}/plan/integration/list`, { page });
                if (res?.response?.status >= 400) {
                    setIntegrations([]);
                } else {
                    const raw = res?.data;
                    const list = Array.isArray(raw) ? raw : raw?.data ?? [];
                    setIntegrations([...list]);
                    setPagination({
                        pages: raw?.pages ?? raw?.last_page ?? 0,
                        records: raw?.records ?? raw?.total ?? list.length,
                        page: raw?.page ?? raw?.current_page ?? page,
                    });
                }
            }
        } catch (e) {
            console.log(e);
        }
        setLoading(false);
    }, [info?.id]);

    function handleOpenCreate() {
        manageModalRef.current?.openCreate(() => handleLoad(1));
    }

    useEffect(() => {
        if (!setPageActions) return;
        setPageActions(
            <Button
                text="Criar integração"
                endIcon={<AddRounded />}
                onClick={handleOpenCreate}
                color="inverse"
                size="small"
            />
        );
        return () => setPageActions(null);
    }, []);

    function handleOpenOptions(event, integration) {
        const options = [
            {
                id: 'edit',
                label: 'Editar integração',
                icon: <EditRounded fontSize="small" />,
                onClick: async () => {
                    popperRef.current.close();
                    manageModalRef.current?.openEdit(integration, () => handleLoad(pagination.page));
                }
            },
            {
                id: 'logs',
                label: 'Ver logs de execução',
                icon: <ListAltRounded fontSize="small" />,
                onClick: async () => {
                    popperRef.current.close();
                    logsModalRef.current?.open(integration);
                }
            },
            {
                id: 'delete',
                label: 'Apagar integração',
                icon: <DeleteRounded fontSize="small" />,
                variant: 'danger',
                onClick: async () => {
                    popperRef.current.close();
                    handleDelete(integration);
                }
            },
        ];
        popperRef.current.open(options, 'bottom', event.currentTarget);
    }

    async function handleToggle(integration) {
        const previousActive = !!integration.active;

        // Optimistically update the UI
        setIntegrations(prev => prev.map(item =>
            item.id === integration.id ? { ...item, active: !previousActive } : item
        ));

        try {
            const res = await api.patch(`integration/${integration.id}/toggle`);
            if (res?.response?.status >= 400) {
                throw new Error(res?.response?.data?.message || 'Erro ao alternar status.');
            }
            window.setSnackbar(`Integração ${!previousActive ? 'ativada' : 'desativada'} com sucesso`);
        } catch (e) {
            console.error(e);
            window.setSnackbar(e.message || 'Não foi possível alterar o status da integração');
            // Revert state if API call fails
            setIntegrations(prev => prev.map(item =>
                item.id === integration.id ? { ...item, active: previousActive } : item
            ));
        }
    }

    function handleDelete(integration) {
        window.openConfirm({
            title: "Apagar integração",
            headline: "Atenção!",
            subtitle: `Você deseja apagar permanentemente a integração "${integration.name}"?`,
            buttons: [
                {
                    text: "Apagar",
                    variant: "contained",
                    color: "error",
                    onClick: async () => {
                        try {
                            const res = await api.delete(`integration/${integration.id}`);
                            if (res?.response?.status >= 400) {
                                window.setSnackbar(res?.response?.data?.message || "Não foi possível apagar a integração");
                                return;
                            }
                            window.setSnackbar("Integração apagada");
                            await handleLoad(pagination.page);
                        } catch (e) {
                            console.log(e);
                        }
                    }
                },
                { text: "Cancelar", variant: "text" }
            ]
        });
    }

    return {
        loading,
        communityLoading: false,
        integrations,
        pagination,
        popperRef,
        manageModalRef,
        logsModalRef,
        community: info,
        handleLoad,
        handleToggle,
        handleDelete,
        handleOpenCreate,
        handleOpenOptions,
    };
}
